import { db } from "@/main";
import { 
  ProjectEventLog, 
  ProjectLogData, 
  ProjectNoteLog, 
  ProjectSyncedNotes, 
  shouldCreateProjectEventLog 
} from "../models/ProjectLog";
import { getProjectRef } from "./ProjectRepository";
import { NoteChange } from "../models/ProjectNote";
import { ProjectSyncTarget } from "../models/ProjectDetails";
import { buildSyncedNoteUpdate } from "@/utils/SyncedNoteUpdate";
import { ProjectStatusSyncStatus } from "../models/ProjectState";
import { firestore } from "firebase";

function getProjectLogDataRef(
  projectId: string,
) {
  const ref = getProjectRef(projectId);

  return db
    .doc(ref)
    .collection("logs")
    .doc("data");
}

function getProjectSyncedNotesDataRef(
  projectId: string,
) {
  const ref = getProjectRef(projectId);

  return db
    .doc(ref)
    .collection("syncedNotes")
    .doc("data");
}

export function observeProjectLogs(
  projectId: string,
  onNext: (projectLogsData: ProjectLogData) => void
) {
  return getProjectLogDataRef(projectId).onSnapshot((snapshot) => {
    onNext(snapshot.data() as ProjectLogData);
  });
}

export function observeProjectSyncedNotes(
  projectId: string,
  onNext: (projectSyncedNotesData: ProjectSyncedNotes) => void
) {
  return getProjectSyncedNotesDataRef(projectId).onSnapshot((snapshot) => {
    onNext(snapshot.data() as ProjectSyncedNotes);
  });
}

export async function addProjectEventLog(
  projectId: string,
  projectEventLog: ProjectEventLog,
) {
  if (!shouldCreateProjectEventLog()) {
    return;
  }

  const logsDataRef = getProjectLogDataRef(projectId);
  const eventId = projectEventLog.id;

  try {
    // Prepare the update object
    const eventLogUpdate: Record<string, ProjectEventLog> = {};
    eventLogUpdate[`eventLogs.${eventId}`] = projectEventLog;

    // Merge the new event log into the existing eventLogs map
    await logsDataRef.update(eventLogUpdate);
  } catch (error) {
    // [SKIP] Error
  }
}

export async function saveProjectNoteLog(
  projectId: string,
  projectNoteLog: ProjectNoteLog,
) {
  const logsDataRef = getProjectLogDataRef(projectId);
  const noteId = projectNoteLog.id;

  try {
    // Prepare the update object
    const noteLogUpdate: Record<string, ProjectNoteLog> = {};
    noteLogUpdate[`noteLogs.${noteId}`] = projectNoteLog;

    // Merge the new event log into the existing eventLogs map
    await logsDataRef.update(noteLogUpdate);

    return true;
  } catch (error) {
    // [SKIP] Error
    return false;
  }
}

export async function deleteProjectNoteLog(
  projectId: string,
  noteId: string
): Promise<boolean> {
  const logsDataRef = getProjectLogDataRef(projectId);

  try {
    // Prepare the update object to delete the note log
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const noteLogUpdate: Record<string, any> = {};
    noteLogUpdate[`noteLogs.${noteId}`] = firestore.FieldValue.delete();

    // Update the database to delete the note log field
    await logsDataRef.update(noteLogUpdate);

    return true;
  } catch (error) {
    // [SKIP] Error
    return false;
  }
}

export async function saveSyncedProjectNote(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  projectNoteLog: ProjectNoteLog | null,
  projectId: string,
  noteId: string,
  noteChange: NoteChange,
  projectSyncTarget?: ProjectSyncTarget
) {
  if (!noteId) {
    return null;
  }

  // Payload rules live in buildSyncedNoteUpdate so this writer and
  // ProjectNotesRepository.updateNote cannot drift apart.
  const update = buildSyncedNoteUpdate(
    noteId,
    projectNoteLog,
    noteChange,
    ProjectStatusSyncStatus.NEEDS_SYNC,
    projectSyncTarget
  );

  try {
    await getProjectSyncedNotesDataRef(projectId).set(update, { merge: true });
    return true;
  } catch (error) {
    // [SKIP] Error
    return false;
  }
}